/** Quick reconciliation for the subscription's account (Ventrata): every ~5 min
 *  the vendor API is polled for changes since the last run and whatever the
 *  webhooks / notifications missed is imported. The drawer shows the flag the
 *  subscription type maps to (BOOKING_UPDATE → reconcile_bookings_enabled,
 *  ORDER_UPDATE → reconcile_orders_enabled) and lets ops toggle it; the change
 *  is saved separately (its own button) to the subscription's OWN account.
 *
 *  The master switch (`reconcile_enabled`) is shown read-only next to the
 *  toggle: with it off, nothing is reconciled whatever the flag says.
 *
 *  Remount with a `key` on the saved value to reset the draft after a save.
 */
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as subscriptionsApi from '@/api/notificationSubscriptions';
import type { ReconcileFlagSource, ReconcileFlagState } from '@/api/notificationSubscriptions';
import { useSnackbar } from '@/components/Snackbar';

const mono = { fontFamily: 'monospace', fontSize: 12 } as const;

const SOURCE_LABELS: Record<ReconcileFlagSource, string> = {
  account: 'set on this account',
  default_account: 'inherited from the default account',
  schema: 'default (not set)',
};

const NOUNS: Record<string, string> = { BOOKING_UPDATE: 'bookings', ORDER_UPDATE: 'orders' };

interface Props {
  subscriptionId: number;
  notificationType: string;
  /** Account slug; empty = the default account. */
  account: string;
  state: ReconcileFlagState;
  canManage: boolean;
  /** Invalidate the detail/grid after a save. */
  onSaved: () => void;
}

export function ReconcileFlagSection({
  subscriptionId,
  notificationType,
  account,
  state,
  canManage,
  onSaved,
}: Props) {
  const snackbar = useSnackbar();
  const [draft, setDraft] = useState(state.enabled);
  const noun = NOUNS[notificationType] ?? 'updates';
  const dirty = draft !== state.enabled;

  const saveMutation = useMutation({
    mutationFn: (enabled: boolean) => subscriptionsApi.setReconcileFlag(subscriptionId, enabled),
    onSuccess: (_detail, enabled) => {
      snackbar.show(`Reconcile ${noun} ${enabled ? 'on' : 'off'} — saved.`, 'success');
      onSaved();
    },
  });
  const saveError = saveMutation.error;

  return (
    <Stack spacing={1}>
      <Typography variant="subtitle2">Quick reconciliation</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
        Every ~5 min the vendor is polled for {noun} changed since the last run, and whatever the
        webhooks or notifications missed is imported. Applies to the{' '}
        {account ? (
          <>
            <Box component="span" sx={mono}>
              {account}
            </Box>{' '}
            account
          </>
        ) : (
          'default account'
        )}
        .
      </Typography>

      <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
        <FormControlLabel
          control={
            <Switch
              checked={draft}
              onChange={(e) => setDraft(e.target.checked)}
              disabled={!canManage || saveMutation.isPending}
            />
          }
          label={`Reconcile ${noun}`}
        />
        {state.master_flag && (
          <Tooltip
            title={`Master switch for this account — read-only here (${
              state.master_source ? SOURCE_LABELS[state.master_source] : 'not configured'
            }). When off, nothing is reconciled.`}
          >
            <Chip
              size="small"
              variant="outlined"
              color={state.master_enabled ? 'success' : 'warning'}
              label={`${state.master_flag}: ${state.master_enabled ? 'on' : 'off'}`}
              sx={{ height: 22, fontSize: 12, fontFamily: 'monospace' }}
            />
          </Tooltip>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {canManage && (
          <Button
            size="small"
            variant="contained"
            disabled={!dirty || saveMutation.isPending}
            onClick={() => saveMutation.mutate(draft)}
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        )}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        <Box component="span" sx={mono}>
          {state.flag}
        </Box>
        : {SOURCE_LABELS[state.source]}
        {dirty ? ' — unsaved change' : ''}
      </Typography>
      {state.inherited_by.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Accounts without their own value inherit this one: {state.inherited_by.join(', ')}.
        </Typography>
      )}

      {state.in_effect ? (
        <Alert severity="success" sx={{ py: 0 }}>
          Reconciliation of {noun} is in effect for this account.
        </Alert>
      ) : !state.master_enabled ? (
        <Alert severity="warning" sx={{ py: 0 }}>
          Quick reconciliation is off for this account ({state.master_flag} = false), so {noun} are
          not reconciled whatever this flag says.
        </Alert>
      ) : (
        <Alert severity="info" sx={{ py: 0 }}>
          Not in effect: {noun} are not reconciled for this account.
        </Alert>
      )}

      {saveError != null && (
        <Alert severity="error" onClose={() => saveMutation.reset()}>
          Could not save:{' '}
          {saveError instanceof ConsoleApiError ? saveError.message : 'request failed'}
        </Alert>
      )}
    </Stack>
  );
}
